import React, { useRef, useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { UpdateCPSRequest } from "helpers/backend_helper"
import logolight from "assets/images/logo-light.png"

const Overview = ({
  entry,
  printBtn,
  shipments = [],
  productionOrders = [],
  reload,
  canCancel = false,
  onCancel,
  isCancelling = false,
}) => {
  const componentRef = useRef()
  const [isEditingStock, setIsEditingStock] = useState(false)
  const [stockValues, setStockValues] = useState({
    min: entry?.safetyStockMin || "",
    max: entry?.safetyStockMax || "",
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isEditingOrderNumbers, setIsEditingOrderNumbers] = useState(false)
  const [orderNumberValues, setOrderNumberValues] = useState({
    orderNumber: entry?.orderNumber || "",
    customerOrderNumber: entry?.customerOrderNumber || "",
  })
  const [isSavingOrderNumbers, setIsSavingOrderNumbers] = useState(false)

  // Update state when entry changes
  useEffect(() => {
    setOrderNumberValues({
      orderNumber: entry?.orderNumber || "",
      customerOrderNumber: entry?.customerOrderNumber || "",
    })
  }, [entry?.orderNumber, entry?.customerOrderNumber])

  // Calculate current stock for annual requests
  const calculateCurrentStock = () => {
    if (entry?.needType !== "annual") return null

    // Sum of delivered shipments
    const deliveredQuantity = shipments
      .filter(shipment => shipment.delivered)
      .reduce((total, shipment) => total + (shipment.quantity || 0), 0)

    // Sum of completed production orders
    const completedQuantity = productionOrders
      .filter(order => order.certificateURL) // Completed orders have certificate
      .reduce((total, order) => total + (order.quantity || 0), 0)

    // Sum of subrequest quantities
    const subRequestQuantity = (entry?.subRequests || [])
      .reduce((total, subRequest) => total + (subRequest.quantity || 0), 0)

    // Quantity used for samples:
    // as soon as a sample report was uploaded, treat sampleQuantity as consumed
    const hasSampleReport =
      !!entry?.dateSampleReportUploaded || !!entry?.sampleReportURL

    const sampleUsed =
      entry?.sampleRequired == 1 && hasSampleReport && entry?.sampleQuantity
        ? entry.sampleQuantity
        : 0

    // Current stock = base quantity + delivered - completed - subrequests - samples used
    return (
      (entry?.quantity || 0) +
      deliveredQuantity -
      completedQuantity -
      subRequestQuantity -
      sampleUsed
    )
  }

  const currentStock = calculateCurrentStock()

  const pricePerUnit =
    entry?.price && entry?.quantity
      ? entry.price / entry.quantity
      : null

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  })

  const getPageMargins = () => {
    return `@page { margin: 50px 120px !important; } @media print { .no-print { display: none; } }`
  }

  const handleStockSave = async () => {
    setIsSaving(true)
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false)
      setIsEditingStock(false)
      // In real implementation, this would update the entry object
      alert("Safety stock levels updated successfully!")
    }, 1000)
  }

  const handleStockCancel = () => {
    setStockValues({
      min: entry?.safetyStockMin || "",
      max: entry?.safetyStockMax || "",
    })
    setIsEditingStock(false)
  }

  const handleSaveOrderNumbers = async () => {
    setIsSavingOrderNumbers(true)
    try {
      const updateData = {
        id: entry.id,
        orderNumber: orderNumberValues.orderNumber || null,
        customerOrderNumber: orderNumberValues.customerOrderNumber || null,
        ...entry,
      }

      await UpdateCPSRequest(updateData)
      setIsEditingOrderNumbers(false)
      // Reload data to reflect changes
      if (reload) reload()
    } catch (error) {
      console.error("Error updating order numbers:", error)
      // You might want to show an error message to the user here
    } finally {
      setIsSavingOrderNumbers(false)
    }
  }

  const handleOrderNumbersCancel = () => {
    setOrderNumberValues({
      orderNumber: entry?.orderNumber || "",
      customerOrderNumber: entry?.customerOrderNumber || "",
    })
    setIsEditingOrderNumbers(false)
  }

  return (
    <Card className="">
      <CardBody className="pb-0">
        <div ref={componentRef}>
          <div className="table-responsive mt-4">
            <style>{getPageMargins()}</style>
            <img
              src={logolight}
              alt=""
              width="200"
              className="mb-4"
              style={{
                maxWidth: "200px",
                marginLeft: "auto",
                marginRight: "auto",
                display: "block",
              }}
            />
            <table className="table">
              <tbody>
                <tr>
                  <th scope="col">Request Number</th>
                  <td scope="col">{entry?.requestNumber}</td>
                </tr>
                {entry?.parentRequestNumber && (
                  <tr>
                    <th scope="col">Parent Request</th>
                    <td scope="col">
                      <Link
                        to={`/cps-requests/${
                          entry.parentRequestID || entry.parentID
                        }`}
                        className="text-primary fw-bold"
                      >
                        {entry.parentRequestNumber}
                      </Link>
                      {entry.parentQuantity && (
                        <div className="text-muted small mt-1">
                          Parent Quantity:{" "}
                          {entry.parentQuantity.toLocaleString()} pcs
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="col">Order Number CPS</th>
                  <td scope="col">
                    {isEditingOrderNumbers ? (
                      <Input
                        type="text"
                        value={orderNumberValues.orderNumber}
                        onChange={e =>
                          setOrderNumberValues({
                            ...orderNumberValues,
                            orderNumber: e.target.value,
                          })
                        }
                        placeholder="Enter order number"
                        className="form-control-sm"
                      />
                    ) : (
                      <div className="d-flex align-items-center">
                        <span>{entry?.orderNumber || "-"}</span>
                        <Button
                          color="link"
                          size="sm"
                          className="ms-2 p-0"
                          onClick={() => setIsEditingOrderNumbers(true)}
                        >
                          <i className="bx bx-edit"></i>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="col">Customer Order Number</th>
                  <td scope="col">
                    {isEditingOrderNumbers ? (
                      <Input
                        type="text"
                        value={orderNumberValues.customerOrderNumber}
                        onChange={e =>
                          setOrderNumberValues({
                            ...orderNumberValues,
                            customerOrderNumber: e.target.value,
                          })
                        }
                        placeholder="Enter customer order number"
                        className="form-control-sm"
                      />
                    ) : (
                      <div className="d-flex align-items-center">
                        <span>{entry?.customerOrderNumber || "-"}</span>
                        <Button
                          color="link"
                          size="sm"
                          className="ms-2 p-0"
                          onClick={() => setIsEditingOrderNumbers(true)}
                        >
                          <i className="bx bx-edit"></i>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
                {isEditingOrderNumbers && (
                  <tr>
                    <td colSpan="2">
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleSaveOrderNumbers}
                          disabled={isSavingOrderNumbers}
                        >
                          {isSavingOrderNumbers ? (
                            <>
                              <i className="bx bx-loader bx-spin me-1"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-check me-1"></i>
                              Save
                            </>
                          )}
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={handleOrderNumbersCancel}
                          disabled={isSavingOrderNumbers}
                        >
                          <i className="bx bx-x me-1"></i>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="col">Service Type</th>
                  <td scope="col">
                    <span
                      className={`badge badge-soft-${
                        entry?.type === "Programming" ? "primary" : "info"
                      }`}
                    >
                      {entry?.type}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th scope="col">Article Description</th>
                  <td scope="col">{entry?.articleDescription}</td>
                </tr>

                {/* Additional Product Information */}
                <tr>
                  <th scope="row">MPN</th>
                  <td>{entry?.mpn || "-"}</td>
                </tr>
                <tr>
                  <th scope="row">Manufacturer</th>
                  <td>{entry?.manufacturer || "-"}</td>
                </tr>
                <tr>
                  <th scope="row">Package Type</th>
                  <td>{entry?.packageType || "-"}</td>
                </tr>
                <tr>
                  <th scope="row">Delivery Form</th>
                  <td>
                    <span className="badge badge-soft-info text-capitalize">
                      {entry?.deliveryForm || "-"}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Supply Form</th>
                  <td>
                    <span className="badge badge-soft-secondary text-capitalize">
                      {entry?.supplyForm || "-"}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th scope="col">Need Type</th>
                  <td scope="col" className="text-capitalize">
                    {entry?.needType?.replace("-", " ")}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Quantity</th>
                  <td>{entry?.quantity} pcs</td>
                </tr>
                {entry?.needType === "annual" && currentStock !== null && (
                  <tr>
                    <th scope="row">Current Stock</th>
                    <td>
                      <span
                        className={`badge badge-soft-${
                          currentStock >= (entry?.safetyStockMin || 0)
                            ? currentStock <=
                              (entry?.safetyStockMax || Infinity)
                              ? "success"
                              : "warning"
                            : "danger"
                        }`}
                      >
                        {currentStock.toLocaleString()} pcs
                      </span>
                      {entry?.safetyStockMin && entry?.safetyStockMax && (
                        <small className="text-muted ms-2">
                          (Min: {entry.safetyStockMin}, Max:{" "}
                          {entry.safetyStockMax})
                        </small>
                      )}
                    </td>
                  </tr>
                )}
                {entry?.type !== "Programming" && (
                  <tr>
                    <th scope="row">Safety Stock</th>
                    <td>
                      {isEditingStock ? (
                        <Form>
                          <div className="row">
                            <div className="col-md-6">
                              <FormGroup>
                                <Label size="sm">Min Level</Label>
                                <Input
                                  type="number"
                                  size="sm"
                                  value={stockValues.min}
                                  onChange={e =>
                                    setStockValues({
                                      ...stockValues,
                                      min: e.target.value,
                                    })
                                  }
                                  placeholder="Min stock level"
                                  min="0"
                                />
                              </FormGroup>
                            </div>
                            <div className="col-md-6">
                              <FormGroup>
                                <Label size="sm">Max Level</Label>
                                <Input
                                  type="number"
                                  size="sm"
                                  value={stockValues.max}
                                  onChange={e =>
                                    setStockValues({
                                      ...stockValues,
                                      max: e.target.value,
                                    })
                                  }
                                  placeholder="Max stock level"
                                  min="0"
                                />
                              </FormGroup>
                            </div>
                          </div>
                          <div className="d-flex gap-2 mt-2">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleStockSave}
                              disabled={isSaving}
                            >
                              {isSaving ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleStockCancel}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Form>
                      ) : (
                        <div className="d-flex justify-content-between align-items-center">
                          <span>
                            Min: {entry?.safetyStockMin || "Not set"} | Max:{" "}
                            {entry?.safetyStockMax || "Not set"}
                          </span>
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => setIsEditingStock(true)}
                            className="no-print"
                          >
                            <i className="bx bx-edit-alt"></i>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Customer Supply</th>
                  <td>
                    <span
                      className={`badge badge-soft-${
                        entry?.customerSupply ? "success" : "warning"
                      }`}
                    >
                      {entry?.customerSupply ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
                {!entry?.customerSupply && entry?.supplier && (
                  <>
                    <tr>
                      <th scope="row">Supplier</th>
                      <td>{entry?.supplier}</td>
                    </tr>
                    <tr>
                      <th scope="row">MPN</th>
                      <td>{entry?.mpn}</td>
                    </tr>
                    <tr>
                      <th scope="row">Manufacturer</th>
                      <td>{entry?.manufacturer}</td>
                    </tr>
                    <tr>
                      <th scope="row">Unit Price</th>
                      <td>
                        {entry?.price
                          ? entry.price.toFixed(2) + " " + entry.currency
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Delivery Time</th>
                      <td>{entry?.deliveryTime || "-"}</td>
                    </tr>
                  </>
                )}
                <tr>
                  <th scope="row">Sample Required</th>
                  <td>
                    <span
                      className={`badge badge-soft-${
                        entry?.sampleRequired ? "info" : "secondary"
                      }`}
                    >
                      {entry?.sampleRequired ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>

                {entry?.sampleRequired == 1 && (
                  <>
                    <tr>
                      <th scope="row">Sample Approved</th>
                      <td>
                        <span
                          className={`badge badge-soft-${
                            entry?.sampleApprovedDate ? "success" : "secondary"
                          }`}
                        >
                          {entry?.sampleApprovedDate ? "Yes" : "No"}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Sample Approved By</th>
                      <td>{entry?.sampleApprovedBy || "-"}</td>
                    </tr>
                    <tr>
                      <th scope="row">Sample Approved On</th>
                      <td>
                        {entry?.sampleApprovedDate
                          ? new Date(
                              entry.sampleApprovedDate
                            ).toLocaleDateString("de-DE", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </td>
                    </tr>
                  </>
                )}

                <tr>
                  <th scope="row">Pricing</th>
                  <td>
                    {entry?.price ? (
                      <div>
                        {pricePerUnit && (
                          <div>
                            Price per Unit:{" "}
                            <span className="text-primary fw-bold">
                              {pricePerUnit.toFixed(2)}{" "}
                              {entry?.currency || "EUR"}
                            </span>
                          </div>
                        )}
                        <div>
                          Total Order Price:{" "}
                          <span className="text-primary fw-bold">
                            {entry.price.toFixed(2)}{" "}
                            {entry?.currency || "EUR"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>

                {/* Sample Information */}
                {entry?.sampleRequired == 1 && (
                  <>
                    <tr>
                      <th scope="row">Sample Quantity</th>
                      <td>
                        {entry?.sampleQuantity
                          ? `${entry.sampleQuantity} pcs`
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Total Sample Price</th>
                      <td>
                        {entry?.samplePrice ? (
                          <span className="text-primary fw-bold">
                            {entry.samplePrice} {entry?.currency || "EUR"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Sample Delivery Date</th>
                      <td>
                        {entry?.sampleDeliveryDate
                          ? new Date(
                              entry.sampleDeliveryDate
                            ).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>

                    {/* Sample Tracking Information */}
                    {entry?.sampleTrackingNumber && (
                      <tr>
                        <th scope="row">Sample Tracking</th>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bx bx-package me-2"></i>
                            <span className="text-primary fw-bold">
                              {entry.sampleTrackingNumber}
                            </span>
                            {entry.sampleShippingService && (
                              <div className="text-muted small ms-2">
                                via {entry.sampleShippingService}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Sample Return Tracking Information */}
                    {entry?.sampleReturnTrackingNumber && (
                      <tr>
                        <th scope="row">Sample Return Tracking</th>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bx bx-package me-2"></i>
                            <span className="text-success fw-bold">
                              {entry.sampleReturnTrackingNumber}
                            </span>
                            {entry.sampleReturnShippingService && (
                              <div className="text-muted small ms-2">
                                via {entry.sampleReturnShippingService}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
                {entry?.instructionURL && (
                  <tr>
                    <th scope="row">Instructions</th>
                    <td>
                      <div className="d-flex align-items-center">
                        {entry.instructionURL && (
                          <a
                            href={entry.instructionURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bx bx-link-external me-1"></i>
                            View Instructions
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="col">Status</th>
                  <td scope="col">
                    <span
                      className={`badge badge-soft-${
                        entry?.status === "Completed"
                          ? "success"
                          : entry?.status === "In Progress"
                          ? "info"
                          : entry?.status === "Offer Pending"
                          ? "warning"
                          : "secondary"
                      }`}
                    >
                      {entry?.status}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th scope="col">Requestor</th>
                  <td scope="col">{entry?.requestor?.name}</td>
                </tr>
                <tr>
                  <th scope="col">CPS Lab</th>
                  <td scope="col">{entry?.cpsLab?.name}</td>
                </tr>
                <tr>
                  <th scope="col">Location Requestor</th>
                  <td scope="col">
                    {entry?.requestor?.street}
                    <br />
                    {entry?.requestor?.postalCode +
                      " " +
                      entry?.requestor?.city}
                    <br />
                    {entry?.requestor?.country +
                      ", " +
                      entry?.requestor?.region}
                  </td>
                </tr>
                <tr>
                  <th scope="col">Location CPS Lab</th>
                  <td scope="col">
                    {entry?.cpsLab?.street}
                    <br />
                    {entry?.cpsLab?.postalCode + " " + entry?.cpsLab?.city}
                    <br />
                    {entry?.cpsLab?.country + " " + entry?.cpsLab?.region}
                  </td>
                </tr>
                {entry?.notes && (
                  <tr>
                    <th scope="row">Notes</th>
                    <td>{entry.notes}</td>
                  </tr>
                )}

                {/* Dates */}
                <tr>
                  <th scope="row">Created</th>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="bx bx-calendar me-2"></i>
                      <span>
                        {entry?.dateCreated
                          ? new Date(entry.dateCreated).toLocaleDateString(
                              "de-DE",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "-"}
                      </span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Last Updated</th>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="bx bx-time me-2"></i>
                      <span>
                        {entry?.dateUpdated
                          ? new Date(entry.dateUpdated).toLocaleDateString(
                              "de-DE",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "-"}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-center">
            <span className="ms-2 text-muted">* All prices excluding VAT</span>
          </div>
        </div>
        <div className="d-flex justify-content-center mt-3 mb-3">
          {printBtn && (
            <>
              <Link
                className="btn btn-primary"
                to="#"
                onClick={handlePrint}
              >
                Print Request
              </Link>
              {canCancel && (
                <Button
                  color="danger"
                  className="ms-2"
                  onClick={onCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? "Cancelling..." : "Cancel Request"}
                </Button>
              )}
            </>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

export default Overview
